import React, { Component } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { get } from 'lodash';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';

import '@openfonts/bellota_all';
import 'font-awesome/css/font-awesome.min.css';
import 'react-toastify/dist/ReactToastify.css';
import 'simple-line-icons/css/simple-line-icons.css';
import 'typeface-gilda-display';
import 'typeface-inter';
import 'typeface-josefin-sans';
import 'typeface-lato';
import 'typeface-montserrat';
import 'typeface-playfair-display';
import 'typeface-roboto';
import 'typeface-roboto-slab';
import 'scss/style.css';

import { CancelSelectionModal } from 'components/CancelSelectionModal';
import { AppModules } from 'modules';
import { InfoModal } from 'components/InfoModal';
import { PasswordPage } from 'components/PasswordPage';
import { ShareModal } from 'components/ShareModal';
import { Tracking } from 'components/Tracking';
import { UpsellModal } from 'components/UpsellModal';
import { applyDesignVariablesToBody, toggleNewDesignClass } from 'helpers/bodyClassName';
import { resolveDesignVariables } from 'helpers/designVariables';
import { getImageUrls } from 'helpers/imageUrls';
import { preloadImage } from 'helpers/preloader';
import { getSortedSliderImages } from 'helpers/sliderImages';
import { AppInstallationAlertHint } from './components/AppInstallationAlertHint';
import { addMetaIconTags, getAppIcon } from './helper';
import { clearAllCookies, getCookiesByteSize, getCookie } from 'helpers/cookie';
import * as actions from 'old-store/actions';
import * as photobookActions from 'modules/ScrPhotobook/store/slices/photobook/actions';
import { ACTIONS, SCOPES } from './old-store/constants/statistics';
import history from 'modules/ScrAppBook/history';
import { DesignThemeProvider } from 'components/DesignThemeProvider';
import { IconSetProvider } from 'components/icons/IconSetProvider';
import { SelectionActionsModal } from 'components/SelectionActionsModal';
import { SelectionDownloadResolutionModal } from 'components/SelectionDownloadResolutionModal';
import { Message } from 'components/Messages';
import { DefaultLoader } from 'components/Loaders/DefaultLoader';
import AppInstallationScrAppBookPage from 'components/AppInstallationScrAppBookPage';
import { AppUpdateBanner } from 'components/AppUpdateBanner';
import { createFontLink } from 'modules/ScrShopNew/store/utils/helpers/createFontLink';

import { DiscountModal } from 'modules/ScrShopNew/components/DiscountModal';

import { fetchCustomerToken } from 'store/slices/selection/actions';
import { isTouchDevice } from 'helpers/deviceDetection';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { MobileDragLayer } from 'modules/ScrPhotobook/pages/PhotobookEdit/EditorContent/MobileDragLayer';
import DesktopDragLayer from 'modules/ScrPhotobook/pages/PhotobookEdit/EditorContent/DesktopDragLayer';
import * as shopReducers from 'modules/ScrShop/store/reducers';
import { reducers as rtkShopReducers } from 'modules/ScrShopNew/store';
import { createRootReducer, store } from 'old-store/store';

// Type imports
import { ICollectionState } from 'store/slices/collection';
// Using any for images state since IImagesState is not exported
import { ITemplatesReducerState } from 'old-store/reducers/templates';
import { IImage } from 'old-store/types/gallery';
import { TemplateFont } from 'old-store/types/templates';
import { trackCollectionView } from 'store/slices/messages/actions';
import { LegacyGlobalModalHost } from 'modules/ScrAppBook/GlobalModalHost';
import {
  SELECTION_SAVED_TOAST_ID,
  shouldKeepSelectionSavedToast
} from 'helpers/selectionSavedToast';

toast.configure();

const DismissSelectionSavedToastOnNavigation = () => {
  const location = useLocation();
  const previousLocation = React.useRef(location);

  React.useEffect(() => {
    if (previousLocation.current !== location) {
      if (!shouldKeepSelectionSavedToast(location.state)) toast.dismiss(SELECTION_SAVED_TOAST_ID);
      previousLocation.current = location;
    }
  }, [location]);

  return null;
};

const NewGlobalModalHost = React.lazy(() => import('modules/ScrAppBookNew/GlobalModalHost'));

const allowedPaths = [
  '/photobook/preview',
  '/shop/basket',
  '/shop/shipping',
  '/shop/payment',
  '/shop/confirm',
  '/shop-new/basket',
  '/shop-new/shipping',
  '/shop-new/payment',
  '/shop-new/confirm'
];

interface IEnvironment {
  isRetina: boolean;
  isIDevice: boolean;
  isAndroidDevice: boolean;
  isMobileChrome: boolean;
  isMobileIE: boolean;
  ieVersion: number | false;
  isApp: boolean;
  isMobileDevice: boolean;
  isNewIpad: boolean;
  isFirefox: boolean;
  isSamsungBrowser: boolean;
  isFacebookApp: () => boolean;
  isDev: boolean;
  subdomain: string;
  upperleveldomain: string;
  isBuilder: boolean;
  siteTracking: boolean;
  isPortfolioModule: boolean;
  isPreview: boolean;
  browserName?: string;
  browserVersion?: string;
  isChromeOnIOs: boolean;
  isMobileSafari: boolean;
  iosVersion?: number;
  isNotSafariOnIos: boolean;
  isNotChromeOnAndroid: boolean;
  OS: 'ios' | 'android' | 'windows' | 'unsupported';
  isTablet: boolean;
  displayWebAppInstallation: boolean;
  _ua?: string;
}

// User state interface for old store user reducer
interface IUserState {
  appInstallation: string;
  appInstallationAlertHint?: boolean;
  confirmedPhotoBookReminder?: boolean;
  confirmedSingleImgDlHint?: boolean;
}

interface IAppProps {
  collection: ICollectionState;
  images: { images: IImage[] };
  environment: IEnvironment;
  user: IUserState;
  templates: ITemplatesReducerState;
  // Action creators from old-store/actions and photobook actions
  fetchSlideshows: () => void;
  fetchTemplateFonts: () => void;
  setLoadedFonts: (fonts: string[]) => void;
  getCollectionById: (id: string) => Promise<any>;
  getCollectionFields: (fields: string[]) => Promise<any>;
  getCollectionCreator: () => Promise<void>;
  logStatistics: (data: { action: string; scope: string }) => void;
  getDownloadToken: () => void;
  getEndCustomer: () => void;
  fetchPhotobooks: (data: any) => void;
  fetchLayouts: () => void;
  setCollectionFetchedStatus: (fetched: boolean) => void;
  setAppInstallationState: (state: string) => void;
  setShareLink: (data: { type: string; url: string }) => void;
  trackCollectionView: () => void;
  [key: string]: any;
}

// App component state interface
interface IAppState {
  pathname: string;
  wasShopRemounted: boolean;
}

class App extends Component<IAppProps, IAppState> {
  private shareLinkSet = false;

  constructor(props: IAppProps) {
    super(props);
    this.state = {
      pathname: window.location.pathname,
      wasShopRemounted: false
    };
  }

  UNSAFE_componentWillMount(): void {
    if (process.env.NODE_ENV === 'development') {
      console.log('API_URL:', window.API_URL);
      console.log('CDN_URL:', window.CDN_URL);
      console.log('environment:', this.props.environment);
    }

    if (this.props.environment.isPortfolioModule) return;

    this.loadSiteData();
  }

  componentDidMount(): void {
    try {
      if (this.props.environment.isPortfolioModule) return;

      this.props.fetchSlideshows();
      this.props.fetchTemplateFonts();

      history.listen(({ location }) => {
        if (this.state.pathname !== location.pathname) {
          this.setState({ pathname: location.pathname });
        }
      });

      window.USER_IS_TOUCHING = true;
    } catch (e) {
      console.log(e);
    }
  }

  UNSAFE_componentWillReceiveProps(): void {
    if (this.props.environment.isPortfolioModule) return;

    const fontsToLoad = this.props.templates.fonts.filter(
      (font: TemplateFont) => !this.props.templates.loadedFonts.includes(font.name)
    );

    if (fontsToLoad.length > 0) {
      createFontLink(this.props.templates.fonts);
      this.props.setLoadedFonts(fontsToLoad.map((f: TemplateFont) => f.name));
    }

    if (this.props.templates.photobookFonts.length > 0) {
      createFontLink(this.props.templates.photobookFonts);
    }
  }

  componentDidUpdate(): void {
    if (this.props.environment.isPortfolioModule) return;

    const { collection, images, setShareLink } = this.props;

    const { collectionCreator } = collection || {};
    const isNewDesign = (collectionCreator as any)?.featureFlags?.newDesign === true;

    if (collection._id) {
      const appIcon = collection.showAppInstall ? getAppIcon(images.images) : {};
      addMetaIconTags(appIcon);

      if ((collection as any).custom_domain && !this.shareLinkSet) {
        this.shareLinkSet = true;
        setShareLink({ type: 'collection', url: (collection as any).custom_domain.custom_domain });
      }

      // Reducers could be changed between old and new shop
      // Temporary solution to remount shops
      if ((collectionCreator as any)?.featureFlags && !this.state.wasShopRemounted) {
        const { featureFlags } = collectionCreator as any;

        if (featureFlags?.newShop) {
          store.replaceReducer(createRootReducer(rtkShopReducers));
        } else {
          store.replaceReducer(createRootReducer(shopReducers));
        }

        // Gate the SBK-9080 font redesign behind the per-user `newDesign` flag.
        this.setState({ wasShopRemounted: true });
      }

      toggleNewDesignClass(isNewDesign);

      const { design } = collection;

      if (!design) return;

      // Apply design variables as CSS custom properties to body element
      applyDesignVariablesToBody(resolveDesignVariables(design, isNewDesign), isNewDesign);

      const MAX_COOKIE_SIZE = 4096;

      if (getCookiesByteSize() >= MAX_COOKIE_SIZE) {
        clearAllCookies();
      }

      const fontsToLoad = design.loadFonts.filter(
        (fontName: string) => !this.props.templates.loadedFonts.includes(fontName)
      );
      if (fontsToLoad.length > 0) {
        // Convert font names to TemplateFont objects for createFontLink
        const fontObjects = fontsToLoad.map((fontName: string) => ({ name: fontName, url: '' }));
        createFontLink(fontObjects);
        this.props.setLoadedFonts(fontsToLoad);
      }
    }
  }

  getBaseUrl(): string {
    const { collection } = this.props;

    if (collection.isShareSite) return `/share/${window.SITE_ID}`;
    if (collection._id) return `/${window.SITE_PATH}`;

    return '/';
  }

  async loadSiteData(): Promise<void> {
    const {
      environment: { isBuilder, siteTracking, isApp },
      getCollectionById,
      getCollectionFields,
      logStatistics,
      getDownloadToken,
      getEndCustomer,
      fetchPhotobooks,
      fetchLayouts,
      setCollectionFetchedStatus
    } = this.props;

    let collection: ICollectionState | null = null;

    try {
      console.log('[APP-START] start loadSiteData');

      this.loadAppInstallationState();

      const initialFieldsArr = ['design', 'title', 'templateName', 'type', 'language', 'user'];
      const initialData = await getCollectionFields(initialFieldsArr);

      if (initialData?.user) {
        // A password-protected gallery refuses to mint an end-customer token (403) — the token
        // is only created once the password is entered. That must not abort startup: everything
        // below it, including the getCollectionById call that sets checkPw, would be skipped.
        await fetchCustomerToken(initialData.user).catch((e) =>
          console.log('[APP-START] fetchCustomerToken failed:', e)
        );
      }

      await this.props.getCollectionCreator();
      const getCollectionResult = await getCollectionById(window.SITE_ID);

      if (getCollectionResult) {
        collection = getCollectionResult.collection;

        const { images } = getCollectionResult;

        let titleImages = getSortedSliderImages(images, collection?.galleries || []);

        titleImages = titleImages.filter(
          (i: IImage) =>
            i.extension.toLowerCase() !== 'gif' ||
            (i.extension.toLowerCase() === 'gif' && i.sizeMb < 5)
        );

        if (titleImages.length > 2) {
          titleImages = titleImages.slice(0, 2);
        }

        titleImages = titleImages.map((image: IImage) => {
          const imageUrls = getImageUrls(image, collection?.signedWildCardUrl || '');

          return {
            ...image,
            ...imageUrls
          };
        });

        const imagesPromiseList: Promise<any>[] = [];

        titleImages.forEach((image: IImage) => imagesPromiseList.push(preloadImage(image.url)));

        await Promise.all(imagesPromiseList);
      }
    } catch (e) {
      console.log('[APP-START] loadSiteDataError:', e);
    } finally {
      if (collection) {
        console.log('[APP-START] successRendering rendering will start');

        getDownloadToken();
        getEndCustomer();
        fetchPhotobooks({});
        fetchLayouts();

        setCollectionFetchedStatus(true);

        if (!isBuilder && siteTracking) {
          trackCollectionView();
          logStatistics({
            action: ACTIONS.VIEW,
            scope: collection?.isShareSite ? SCOPES.SHARE : isApp ? SCOPES.APP : SCOPES.COLLECTION // eslint-disable-line
          });
        }
      } else {
        console.log('[APP-START] no rendering ');
      }
    }
  }

  loadAppInstallationState(): void {
    const { setAppInstallationState, environment, logStatistics } = this.props;
    try {
      console.log('[APP-START] start loadAppInstallationState');
      if (environment.isApp) {
        if (getCookie(`appInstallationState-${window.SITE_ID}`) !== 'installed') {
          logStatistics({
            action: ACTIONS.APP_INSTALL,
            scope: SCOPES.APP
          });
        }
        setAppInstallationState('installed');
      } else {
        setAppInstallationState(getCookie(`appInstallationState-${window.SITE_ID}`) || 'unset');
      }
    } catch (e) {
      console.log(e);
      console.log('[APP-START] catch error in app.js loadAppInstallationState =>', e);
      window.logToServer(e);
    }
  }

  renderRoutes(): React.ReactNode {
    const { collection } = this.props;
    const { collectionCreator } = collection || {};
    const { featureFlags } = (collectionCreator as any) || {};

    return (
      <Router basename={this.getBaseUrl()}>
        <IconSetProvider>
          <DismissSelectionSavedToastOnNavigation />
          {featureFlags?.newDesign === true ? (
            <React.Suspense fallback={null}>
              <NewGlobalModalHost />
            </React.Suspense>
          ) : (
            <LegacyGlobalModalHost />
          )}
          {featureFlags?.newDesign !== true && <InfoModal />}
          <ShareModal />
          <Message />
          {featureFlags?.newDesign !== true && <AppInstallationAlertHint />}
          <AppUpdateBanner />
          <CancelSelectionModal />
          <UpsellModal />
          <SelectionActionsModal />
          <SelectionDownloadResolutionModal />
          <DiscountModal />
          <AppModules />
        </IconSetProvider>
      </Router>
    );
  }

  renderContent(): React.ReactNode {
    const { collection, environment, user } = this.props;
    const isShopSection = get(history, 'location.pathname', '').includes('shop');
    const isPhotobookSection = get(history, 'location.pathname', '').includes('photobook');
    const isTouchableDevice = isTouchDevice();

    if (collection.checkPw) {
      const isPathAllowed = allowedPaths.some((path) => window.location.pathname.includes(path));

      if (!isPathAllowed) return <PasswordPage />;
    }

    // page is not loaded yet
    if (!collection.fetched && !environment.isBuilder && !environment.isPortfolioModule) {
      return <DefaultLoader />;
    }

    const shouldMountAppInstallationPrompt =
      !environment.isPreview &&
      !environment.isPortfolioModule &&
      !isShopSection &&
      !isPhotobookSection &&
      !window.CRYPT_IMG_NAME &&
      environment.displayWebAppInstallation &&
      collection.showAppInstall &&
      (user.appInstallation === 'unset' ||
        user.appInstallation === 'accepted' ||
        user.appInstallation === 'forced');

    return (
      <div>
        <DndProvider
          backend={isTouchableDevice ? TouchBackend : HTML5Backend}
          options={{ delayTouchStart: 200 }}
        >
          {this.renderRoutes()}
          <Tracking />
          {isTouchableDevice ? <MobileDragLayer /> : <DesktopDragLayer />}
          {shouldMountAppInstallationPrompt && <AppInstallationScrAppBookPage />}
        </DndProvider>
      </div>
    );
  }

  render(): React.ReactNode {
    return <DesignThemeProvider>{this.renderContent()}</DesignThemeProvider>;
  }
}

interface IRootState {
  collection: ICollectionState;
  images: { images: IImage[] };
  environment: IEnvironment;
  user: IUserState;
  templates: ITemplatesReducerState;
  [key: string]: any;
}

const mapStateToProps = (state: IRootState) => state;

export default connect(mapStateToProps, { ...actions, ...photobookActions })(App as any);
